import React from 'react';
import { List as ImmutableList } from 'immutable';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { FormControlLabel, Grid, MenuItem } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';

import { TDocumentTemplateListItemImt } from 'common/interfaces/dictionary';
import { IScriptListItemImt } from 'modules/crm/interfaces/scripts';
import { CampaignTypes } from 'modules/crm/constants/campaigns';
import inputLabels from 'common/messages/inputLabels';
import { Checkbox, MultipleSelect, Select, TextField } from 'common/components';
import { useRenderIntlMessage } from 'common/hooks/useRenderIntlMessage';
import { CampaignType } from 'common/constants/campaign';
import { ICampaignForm } from '../../../interfaces/campaigns';

interface IProps {
  scripts: ImmutableList<IScriptListItemImt>;
  documentTemplates: ImmutableList<TDocumentTemplateListItemImt>;
  isEditCampaign?: boolean;
}

const CampaignInfoSection = ({
  scripts,
  documentTemplates,
  isEditCampaign,
}: IProps): JSX.Element => {
  const { control, formState } = useFormContext<ICampaignForm>();
  const { errors } = formState;

  const campaignType = useWatch({
    control,
    name: 'campaignType',
  });

  const renderIntlMessage = useRenderIntlMessage();

  const isEmailTemplateFiledVisible = CampaignType.EMAIL === campaignType;
  const isTextTemplateFiledVisible = CampaignType.SMS === campaignType;
  const isAutomaticFiledVisible =
    CampaignType.EMAIL === campaignType || CampaignType.SMS === campaignType;

  const getTemplateOptionsByType = () => {
    return documentTemplates
      .map(script => ({
        id: script.get('id'),
        title: script.get('title'),
      }))
      .toArray();
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="title"
          render={({ field }) => (
            <TextField
              value={field.value}
              onChange={field.onChange}
              label={<FormattedMessage {...inputLabels.title} />}
              variant="outlined"
              autoComplete="off"
              fullWidth
              error={!!errors.title}
              helperText={renderIntlMessage(errors.title?.message)}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Controller
          control={control}
          name="campaignType"
          defaultValue=""
          render={({ field }) => (
            <Select
              fullWidth
              disabled={isEditCampaign}
              label={<FormattedMessage {...inputLabels.type} />}
              variant="outlined"
              name={field.name}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={!!errors.campaignType}
              helperText={renderIntlMessage(errors.campaignType?.message)}
            >
              {CampaignTypes.getSelectOptions()}
            </Select>
          )}
        />
      </Grid>

      {isEmailTemplateFiledVisible || isTextTemplateFiledVisible ? (
        <Grid item xs={12} md={6}>
          <Controller
            control={control}
            name="documentTemplate"
            render={({ field }) => (
              <MultipleSelect
                fullWidth
                label={
                  <FormattedMessage
                    {...(isEmailTemplateFiledVisible
                      ? inputLabels.emailTemplate
                      : inputLabels.smsTemplate)}
                  />
                }
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={!!errors.documentTemplate}
                helperText={renderIntlMessage(errors.documentTemplate?.message)}
                options={getTemplateOptionsByType()}
              />
            )}
          />
        </Grid>
      ) : (
        <Grid item xs={12} md={6}>
          <Controller
            control={control}
            name="script.id"
            defaultValue=""
            render={({ field }) => (
              <Select
                fullWidth
                label={<FormattedMessage {...inputLabels.script} />}
                variant="outlined"
                name={field.name}
                value={field.value === null ? '' : field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={!!errors.script}
                helperText={renderIntlMessage(errors.script?.id?.message)}
              >
                {scripts.map(script => (
                  <MenuItem key={script.get('id')} value={script.get('id')}>
                    {script.get('title')}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </Grid>
      )}

      {isAutomaticFiledVisible && (
        <Grid item xs={12}>
          <Controller
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={field.value}
                    onChange={e => field.onChange(e.target.checked)}
                    color="primary"
                  />
                }
                label={<FormattedMessage {...inputLabels.automaticRun} />}
              />
            )}
            control={control}
            name="automatic"
          />
        </Grid>
      )}
    </Grid>
  );
};

export default React.memo(CampaignInfoSection);
